if (!customElements.get('shoppable-image')) {
  customElements.define(
    'shoppable-image',
    class ShoppableImage extends HTMLElement {
      constructor() {
        super();

        this.hotspots = Array.from(this.querySelectorAll('[data-hotspot]'));
        this.coordinateHelperEnabled = this.dataset.coordinateHelper === 'true';
        this.imageWrapper = this.querySelector('.shoppable-image__image-wrapper');
        this.coordinateMarker = this.querySelector('[data-coordinate-helper-marker]');
        this.coordinateXOutput = this.querySelector('[data-coordinate-helper-x]');
        this.coordinateYOutput = this.querySelector('[data-coordinate-helper-y]');
        this.coordinateModeOutput = this.querySelector('[data-coordinate-helper-mode]');
        this.coordinateCopyButton = this.querySelector('[data-coordinate-helper-copy]');
        this.hotspotToggleButton = this.querySelector('[data-hotspot-toggle]');
        this.focusableSlideElements = Array.from(this.querySelectorAll('.shoppable-image__button, [data-hotspot-card] a, [data-hotspot-toggle]'));
        this.lastCoordinates = null;
        this.pendingSliderPage = null;
        this.pendingSliderScrollLeft = null;
        this.slideObserver = null;
        this.sliderControlsObserver = null;
        this.syncingSliderControls = false;

        this.onCoordinateClick = this.onCoordinateClick.bind(this);
        this.onCoordinateCopy = this.onCoordinateCopy.bind(this);
        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onKeyup = this.onKeyup.bind(this);
        this.onResize = this.onResize.bind(this);
        this.onScroll = this.onScroll.bind(this);
        this.onSlideChanged = this.onSlideChanged.bind(this);
        this.onSliderControlClick = this.onSliderControlClick.bind(this);
        this.onHotspotToggleClick = this.onHotspotToggleClick.bind(this);
      }

      connectedCallback() {
        this.hotspots.forEach((hotspot) => {
          const button = hotspot.querySelector('.shoppable-image__button');
          if (!button) return;

          button.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            this.toggleHotspot(hotspot);
          });
        });

        if (this.hotspotToggleButton) {
          this.hotspotToggleButton.addEventListener('click', this.onHotspotToggleClick);
        }

        this.positionCards();
        this.setupCoordinateHelper();
        this.setupSlideInteractivity();
        document.addEventListener('click', this.onDocumentClick);
        document.addEventListener('keyup', this.onKeyup);
        window.addEventListener('resize', this.onResize);
        window.addEventListener('scroll', this.onScroll, { passive: true });

        this.slideshow = this.closest('slideshow-component');
        if (this.slideshow) {
          this.slideshow.addEventListener('slideChanged', this.onSlideChanged);
          this.setupSliderControls();
        }
      }

      disconnectedCallback() {
        if (this.coordinateHelperEnabled && this.imageWrapper) {
          this.imageWrapper.removeEventListener('click', this.onCoordinateClick);
        }

        if (this.coordinateCopyButton) {
          this.coordinateCopyButton.removeEventListener('click', this.onCoordinateCopy);
        }

        if (this.hotspotToggleButton) {
          this.hotspotToggleButton.removeEventListener('click', this.onHotspotToggleClick);
        }

        document.removeEventListener('click', this.onDocumentClick);
        document.removeEventListener('keyup', this.onKeyup);
        window.removeEventListener('resize', this.onResize);
        window.removeEventListener('scroll', this.onScroll);

        if (this.slideshow) this.slideshow.removeEventListener('slideChanged', this.onSlideChanged);
        if (this.slideObserver) this.slideObserver.disconnect();
        if (this.sliderControlsObserver) this.sliderControlsObserver.disconnect();
      }

      onHotspotToggleClick(event) {
        event.preventDefault();
        event.stopPropagation();

        const shouldHide = !this.classList.contains('shoppable-image--hotspots-hidden');
        this.classList.toggle('shoppable-image--hotspots-hidden', shouldHide);
        this.hotspotToggleButton.setAttribute('aria-pressed', shouldHide ? 'false' : 'true');
        this.hotspotToggleButton.setAttribute('aria-label', shouldHide ? 'Show product hotspots' : 'Hide product hotspots');

        if (shouldHide) this.closeAll();
      }

      toggleHotspot(activeHotspot) {
        const activeButton = activeHotspot.querySelector('.shoppable-image__button');
        const activeCard = this.getHotspotCard(activeHotspot);
        const shouldOpen = activeCard && activeCard.hasAttribute('hidden');

        this.closeAll();

        if (!shouldOpen) return;

        activeHotspot.classList.add('shoppable-image__hotspot--active');
        activeCard.removeAttribute('hidden');
        this.portalCard(activeCard);
        activeButton.setAttribute('aria-expanded', 'true');
        this.positionCard(activeHotspot);
      }

      closeAll() {
        this.hotspots.forEach((hotspot) => {
          const button = hotspot.querySelector('.shoppable-image__button');
          const card = this.getHotspotCard(hotspot);

          if (card) {
            this.restoreCard(card, hotspot);
            card.setAttribute('hidden', '');
          }
          if (button) button.setAttribute('aria-expanded', 'false');
          hotspot.classList.remove('shoppable-image__hotspot--active');
        });
      }

      portalCard(card) {
        card.dataset.shoppableActiveOwner = this.dataset.blockId;
        card.classList.add('shoppable-image__card--active');
        this.getCardPortal().appendChild(card);
      }

      restoreCard(card, hotspot) {
        card.classList.remove('shoppable-image__card--active');
        card.removeAttribute('data-shoppable-active-owner');
        hotspot.appendChild(card);
      }

      getHotspotCard(hotspot) {
        const button = hotspot.querySelector('.shoppable-image__button');
        const cardId = button ? button.getAttribute('aria-controls') : null;

        return cardId ? document.getElementById(cardId) : hotspot.querySelector('[data-hotspot-card]');
      }

      getCardPortal() {
        return document.body;
      }

      setupSlideInteractivity() {
        this.updateSlideInteractivity();

        this.slideObserver = new MutationObserver(() => this.updateSlideInteractivity());
        this.slideObserver.observe(this, {
          attributes: true,
          attributeFilter: ['aria-hidden'],
        });
      }

      updateSlideInteractivity() {
        const isHiddenSlide = this.getAttribute('aria-hidden') === 'true';

        this.focusableSlideElements.forEach((element) => {
          if (isHiddenSlide) {
            element.setAttribute('tabindex', '-1');
          } else {
            element.removeAttribute('tabindex');
          }
        });

        if (isHiddenSlide) this.closeAll();
      }

      setupSliderControls() {
        if (!this.slideshow || this.slideshow.dataset.shoppableSliderControlsBound === 'true') return;

        const slider = this.slideshow.querySelector('[id^="Slider-"]');
        const controls = Array.from(this.slideshow.querySelectorAll('[data-shoppable-slide-index]'));
        if (!slider || !controls.length) return;

        this.slideshow.dataset.shoppableSliderControlsBound = 'true';
        this.slideshow.addEventListener('click', this.onSliderControlClick, true);
        this.slideshow.addEventListener('slideChanged', (event) => this.syncSliderControls(event.detail.currentPage));
        slider.addEventListener('scroll', () => window.requestAnimationFrame(() => this.syncSliderControls()));
        this.observeSliderControls(controls);
        this.syncSliderControls();
        window.requestAnimationFrame(() => this.syncSliderControls());
      }

      observeSliderControls(controls) {
        this.sliderControlsObserver = new MutationObserver(() => {
          if (this.syncingSliderControls) return;
          window.requestAnimationFrame(() => this.syncSliderControls());
        });

        controls.forEach((control) => {
          this.sliderControlsObserver.observe(control, {
            attributes: true,
            attributeFilter: ['class', 'aria-current'],
          });
        });
      }

      onSliderControlClick(event) {
        const target = event.target instanceof Element ? event.target : event.target.parentElement;
        const control = target ? target.closest('[data-shoppable-slide-index]') : null;
        if (!control || !this.slideshow || !this.slideshow.contains(control)) return;

        const slider = this.slideshow.querySelector('[id^="Slider-"]');
        const slideIndex = Number(control.dataset.shoppableSlideIndex);
        const slide = this.slideshow.querySelectorAll('.shoppable-image__slide')[slideIndex - 1];
        if (!slider || !slide) return;

        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        this.pendingSliderPage = slideIndex;
        this.pendingSliderScrollLeft = slide.offsetLeft;

        slider.scrollTo({
          left: slide.offsetLeft,
        });

        this.syncSliderControls(slideIndex);
      }

      syncSliderControls(currentPage = null) {
        if (!this.slideshow) return;

        const slider = this.slideshow.querySelector('[id^="Slider-"]');
        const slides = Array.from(this.slideshow.querySelectorAll('.shoppable-image__slide'));
        const controls = Array.from(this.slideshow.querySelectorAll('[data-shoppable-slide-index]'));
        if (!slider || !slides.length) return;

        const slideWidth = (slides[1] ? slides[1].offsetLeft - slides[0].offsetLeft : slides[0].clientWidth) || 1;
        const reachedPendingSlide = this.pendingSliderScrollLeft !== null && Math.abs(slider.scrollLeft - this.pendingSliderScrollLeft) < 2;

        if (reachedPendingSlide) {
          this.pendingSliderPage = null;
          this.pendingSliderScrollLeft = null;
        }

        const activePage = Math.min(Math.max(this.pendingSliderPage || currentPage || Math.round(slider.scrollLeft / slideWidth) + 1, 1), slides.length);

        this.syncingSliderControls = true;

        controls.forEach((control) => {
          const isActive = Number(control.dataset.shoppableSlideIndex) === activePage;
          control.classList.toggle('slider-counter__link--active', isActive);

          if (isActive) {
            control.setAttribute('aria-current', 'true');
          } else {
            control.removeAttribute('aria-current');
          }
        });

        this.slideshow.querySelectorAll('.slider-counter--current').forEach((counter) => {
          counter.textContent = activePage;
        });

        this.slideshow.querySelectorAll('.slider-counter--total').forEach((counter) => {
          counter.textContent = slides.length;
        });

        this.syncingSliderControls = false;
      }

      positionCards() {
        this.hotspots.forEach((hotspot) => this.positionCard(hotspot));
      }

      positionCard(hotspot) {
        hotspot.classList.remove(
          'shoppable-image__hotspot--top',
          'shoppable-image__hotspot--right',
          'shoppable-image__hotspot--bottom',
          'shoppable-image__hotspot--left'
        );

        const position = this.getCurrentPosition(hotspot);

        if (position.x < 24) {
          hotspot.classList.add('shoppable-image__hotspot--right');
        } else if (position.x > 76) {
          hotspot.classList.add('shoppable-image__hotspot--left');
        } else if (position.y < 34) {
          hotspot.classList.add('shoppable-image__hotspot--bottom');
        } else {
          hotspot.classList.add('shoppable-image__hotspot--top');
        }

        if (hotspot.classList.contains('shoppable-image__hotspot--active')) {
          this.positionActiveCard(hotspot, position);
        }
      }

      positionActiveCard(hotspot, position) {
        const button = hotspot.querySelector('.shoppable-image__button');
        const card = this.getHotspotCard(hotspot);
        if (!button || !card || card.hasAttribute('hidden')) return;

        const gap = 16;
        const viewportPadding = 16;
        const buttonRect = button.getBoundingClientRect();
        const cardRect = card.getBoundingClientRect();
        let left = buttonRect.left + buttonRect.width / 2 - cardRect.width / 2;
        let top = buttonRect.top - cardRect.height - gap;

        if (position.x < 24) {
          left = buttonRect.right + gap;
          top = buttonRect.top + buttonRect.height / 2 - cardRect.height / 2;
        } else if (position.x > 76) {
          left = buttonRect.left - cardRect.width - gap;
          top = buttonRect.top + buttonRect.height / 2 - cardRect.height / 2;
        } else if (position.y < 34) {
          top = buttonRect.bottom + gap;
        }

        const maxLeft = window.innerWidth - cardRect.width - viewportPadding;

        left = Math.min(Math.max(left, viewportPadding), Math.max(maxLeft, viewportPadding));

        card.style.setProperty('--hotspot-card-left', `${left + window.scrollX}px`);
        card.style.setProperty('--hotspot-card-top', `${top + window.scrollY}px`);
      }

      getCurrentPosition(hotspot) {
        const useMobilePosition = hotspot.dataset.useMobilePosition === 'true';
        const isMobile = window.matchMedia('(max-width: 989px)').matches;
        const x = isMobile && useMobilePosition ? hotspot.dataset.mobileX : hotspot.dataset.x;
        const y = isMobile && useMobilePosition ? hotspot.dataset.mobileY : hotspot.dataset.y;

        return {
          x: Number(x) || 50,
          y: Number(y) || 50,
        };
      }

      setupCoordinateHelper() {
        if (!this.coordinateHelperEnabled || !this.imageWrapper) return;

        this.updateCoordinateMode();
        this.imageWrapper.addEventListener('click', this.onCoordinateClick);

        if (this.coordinateCopyButton) {
          this.coordinateCopyButton.addEventListener('click', this.onCoordinateCopy);
        }
      }

      onCoordinateClick(event) {
        if (event.target.closest('[data-hotspot], [data-hotspot-card], .shoppable-image__content, .shoppable-image__coordinate-helper')) return;

        const image = this.querySelector('.shoppable-image__image, .shoppable-image__placeholder');
        if (!image) return;

        const rect = image.getBoundingClientRect();
        const x = ((event.clientX - rect.left) / rect.width) * 100;
        const y = ((event.clientY - rect.top) / rect.height) * 100;
        const boundedX = Math.min(100, Math.max(0, x));
        const boundedY = Math.min(100, Math.max(0, y));

        this.lastCoordinates = {
          x: boundedX.toFixed(1),
          y: boundedY.toFixed(1),
        };

        this.updateCoordinateOutput();
      }

      updateCoordinateOutput() {
        if (!this.lastCoordinates) return;

        if (this.coordinateXOutput) this.coordinateXOutput.textContent = this.lastCoordinates.x;
        if (this.coordinateYOutput) this.coordinateYOutput.textContent = this.lastCoordinates.y;

        if (this.coordinateMarker) {
          this.coordinateMarker.style.setProperty('--coordinate-helper-x', `${this.lastCoordinates.x}%`);
          this.coordinateMarker.style.setProperty('--coordinate-helper-y', `${this.lastCoordinates.y}%`);
          this.coordinateMarker.removeAttribute('hidden');
        }

        if (this.coordinateCopyButton) this.coordinateCopyButton.removeAttribute('disabled');
      }

      onCoordinateCopy(event) {
        event.preventDefault();
        event.stopPropagation();

        if (!this.lastCoordinates) return;

        const text = `X: ${this.lastCoordinates.x}, Y: ${this.lastCoordinates.y}`;

        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(text);
        }

        if (this.coordinateCopyButton) {
          this.coordinateCopyButton.textContent = 'Copied';
          window.setTimeout(() => {
            this.coordinateCopyButton.textContent = 'Copy X/Y';
          }, 1500);
        }
      }

      updateCoordinateMode() {
        if (!this.coordinateModeOutput) return;

        this.coordinateModeOutput.textContent = window.matchMedia('(max-width: 989px)').matches ? 'Mobile' : 'Desktop';
      }

      onDocumentClick(event) {
        const clickedButton = event.target.closest('.shoppable-image__button');
        const clickedCard = event.target.closest('[data-hotspot-card]');

        if (clickedButton && this.contains(clickedButton)) return;
        if (clickedCard && clickedCard.dataset.shoppableActiveOwner === this.dataset.blockId) return;

        this.closeAll();
      }

      onKeyup(event) {
        if (event.code && event.code.toUpperCase() === 'ESCAPE') this.closeAll();
      }

      onResize() {
        this.positionCards();
        this.updateCoordinateMode();
      }

      onScroll() {
        this.positionCards();
      }

      onSlideChanged() {
        this.closeAll();
        this.updateSlideInteractivity();
      }
    }
  );
}
